import { Button, Checkbox, DialogV2, Loader, Typography } from '@recruit-robin/robin-theme/web-components';
import { ChevronLeft, LinkExternal_02, Reply, Star_06 } from '@recruit-robin/robin-theme/web-icons';
import { WhatsAppPreview } from 'components/ui/molecules';
import { jobBoardLabel } from 'helpers/jobBoards';
import { useContext } from 'react';
import { candidateHasEmail } from '@/utils/candidates';
import { buildRjWhatsappJobDetails } from '@/shared/lib/rj-whatsapp-message';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialogData } from 'stores/global-dialogs/hooks/useGlobalDialogData';
import { useWhatsappPreviewMessage } from '../RJCommunicationHub.useWhatsappPreviewMessage';
import { useStyles } from './WhatsAppRjCommDialog.styles';
import { WhatsAppRjCommDialogProps } from './WhatsAppRjCommDialog.types';

export const WhatsAppRjCommDialog = ({
  isVisible,
  onClose,
  onConfirm,
  isLoading = false,
  dontShowWhatsAppMessagePreview = false,
  setDontShowWhatsAppMessagePreview,
  showDontShowWhatsappMessagePreviewCheckbox = true,
  improvedVacancy,
  previewFirstName,
  onGoBack,
}: WhatsAppRjCommDialogProps) => {
  const classes = useStyles({});
  const { dictionary } = useContext(LocalizationContext);
  const whatsappPreviewMessage = useWhatsappPreviewMessage();
  const dialogData = useGlobalDialogData({ isVisible, dialogType: GlobalDialogType.CandidateOutreach });
  const { data: currentUser } = useCurrentUserQuery();
  const tenantName = currentUser?.tenant.name ?? '';
  const vacancyParams = buildRjWhatsappJobDetails(improvedVacancy ?? null);
  const candidate = dialogData?.candidate;

  return (
    <DialogV2.Root isVisible={isVisible} customCss={classes.dialog}>
      <DialogV2.Backdrop />
      <DialogV2.Content>
        <DialogV2.Close onRequestClose={onClose} />
        <DialogV2.Header customCss={classes.title}>
          <Star_06 size={24} color={(c) => c.primary.fuchsia[40]} />
          <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[80]}>
            {dictionary.reviewOutreachMessage}
          </Typography>
        </DialogV2.Header>
        <DialogV2.Body customCss={classes.body}>
          <div css={classes.previewContainer}>
            <WhatsAppPreview
              height={Math.round(window.innerHeight * 0.45)}
              items={[
                {
                  type: 'message',
                  text: whatsappPreviewMessage({
                    candidateFirstName:
                      previewFirstName === undefined ? candidate?.firstName ?? '' : previewFirstName ?? '',
                    recruiterFirstName: currentUser?.first_name ?? '',
                    companyName: tenantName,
                    jobBoardName: candidate?.activeJobBoard ? jobBoardLabel(candidate.activeJobBoard) : '',
                    includeEmailNote: candidate ? candidateHasEmail(candidate) : false,
                    ...vacancyParams,
                  }).replace(/^(Hi|Hoi) ,/, '$1,'),
                },
                {
                  type: 'button',
                  text: dictionary.viewVacancy,
                  icon: <LinkExternal_02 color={(c) => c.secondary.sky[60]} />,
                },
                {
                  type: 'button',
                  text: dictionary.noVacancyAnymore,
                  icon: <Reply size={24} color={(c) => c.secondary.sky[60]} />,
                },
              ]}
            />
          </div>
          {showDontShowWhatsappMessagePreviewCheckbox ? (
            <Checkbox
              label={dictionary.dontShowWhatsappMessagePreviewNextTime}
              checked={dontShowWhatsAppMessagePreview ?? false}
              onClick={(value) => setDontShowWhatsAppMessagePreview?.(value)}
            />
          ) : null}
        </DialogV2.Body>
        <DialogV2.Footer>
          <Button
            startIcon={<ChevronLeft size={16} color={(c) => c.neutral.variant[70]} />}
            label={dictionary.back}
            onClick={onGoBack}
            style="text"
            type="subtle2"
          />
          <Button
            label={dictionary.next}
            onClick={onConfirm}
            style="filled"
            type="success"
            disabled={isLoading}
            startIcon={isLoading ? <Loader size={16} /> : undefined}
          />
        </DialogV2.Footer>
      </DialogV2.Content>
    </DialogV2.Root>
  );
};
